/** @file Home of the root component. */
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Maybe } from '@codegrade/maybe';
import * as button from '@/components/Button';
import * as utils from '@/utils';
import * as constants from '../constants';
import { Message } from './Message';
/**
 * The Root renders a form, reset and submit buttons, and leverage the browser's built-in validation API
 * to make building quality forms a little bit easier.
 */
export const Root = tsx.withHtmlAttrs(tsx.componentFactoryOf().create({
    name: constants.nameRoot,
    props: {
        /* Configure Appearance */
        /**
         * Define the internal layout.
         *
         * When using `stacked`, input labels are above their inputs.
         * When using `row`, input labels are next to their inputs, but can become stacked when the container is too narrow.
         * When using `custom`, developer should style the components via `class` props.
         */
        layout: p.ofStringLiterals('stacked', 'row', 'custom').default('stacked'),
        /** The text label on the submit button. */
        submitLabel: p(String).default('Save'),
        /** If Root should render the submit button. */
        showSubmit: p(Boolean).default(true),
        /** If Root should render the reset button. */
        showReset: p(Boolean).default(false),
        actionBarClass: p.ofType().optional,
        /* Behavior */
        submitOnEnter: p(Boolean).default(false),
        submitOnCtrlOrCommandEnter: p(Boolean).default(false),
        handleSubmit: p.ofFunction().required,
        handleReset: p.ofFunction().optional,
        hasChanges: p.ofType().default(true),
        submitDisabledReason: p.ofType().optional,
        resetDisabledReason: p.ofType().optional,
        confirmReset: p.ofType().default(false),
        confirmSubmit: p.ofType().default(false),
        /* Accessibility requirements */
        submitLoadingMessage: p(String).required,
        submitSuccessMessage: p(String).required,
    },
    /** @returns Internal state of Root. */
    data() {
        return {
            error: Maybe.empty(),
            warnings: Maybe.empty(),
        };
    },
    methods: {
        /**
         * Get the form ref as a HTMLFormElement object.
         *
         * @returns A maybe of a HTMLFormElement.
         */
        getFormElement() {
            return Maybe.fromNullable(utils.getRefAsElement(this.$refs.formElement)).filter(el => el instanceof HTMLFormElement);
        },
        /**
         * Method to validate and submit the form.
         *
         * @returns An ActionPromise object.
         */
        async submitForm() {
            this.error = Maybe.empty();
            // Validate the form, if the form element could be found.
            // In case the form element is not found for some reason,
            // proceed to submit the value and let backend validate and
            // report potential issues.
            const formElement = this.getFormElement();
            if (formElement.isJust()) {
                const form = formElement.value;
                const isFormInputValid = form.checkValidity();
                if (!isFormInputValid) {
                    form.reportValidity();
                    // Do not continue to call handleSubmit.
                    throw new Error('The form contains invalid input(s)');
                }
            }
            return this.handleSubmit();
        },
        /** Method to handle error from submit. */
        handleSubmitError(evt) {
            tsx.emitOn(this, 'onError', evt);
            this.error = Maybe.of(evt);
            setTimeout(() => {
                evt.reset({
                    focusButtonAfterClose: false,
                });
            }, 1000);
        },
        /** Method to handle warnings from submit. */
        handleSubmitWarning(evt) {
            tsx.emitOn(this, 'onWarning', evt);
            this.warnings = Maybe.of(evt);
        },
        /** Reset the form and its additional states like error. */
        resetForm() {
            this.error = Maybe.empty();
            if (this.handleReset !== undefined) {
                this.handleReset();
            }
            tsx.emitOn(this, 'onReset', undefined);
        },
        /**
         * Take `somethingShouldConfirm` prop value and output a valid confirm prop value.
         *
         * @param prop The target prop to create confirm prop value for.
         * @param message The default confirm message.
         * @returns The valid confirm prop value, false or a button.Confirm object.
         */
        propToButtonConfirm(prop, message) {
            if (typeof prop === 'string') {
                return {
                    type: 'dialog',
                    message: prop,
                };
            }
            if (prop === true) {
                return {
                    type: 'dialog',
                    message,
                };
            }
            else {
                return prop;
            }
        },
    },
    computed: {
        /** @returns Internal disabled reason for buttons. */
        maybeDisabledReason() {
            if (this.hasChanges) {
                return Maybe.empty();
            }
            return Maybe.of({
                type: 'tooltip',
                style: 'hard',
                reason: 'Nothing has changed',
            });
        },
        /** @returns The value to pass into the reset button's confirm prop. */
        resetButtonConfirmProp() {
            return this.propToButtonConfirm(this.confirmReset, 'Are you sure you want to reset the form?');
        },
        /** @returns The value to pass into the submit button's confirm prop. */
        submitButtonConfirmProp() {
            return this.propToButtonConfirm(this.confirmSubmit, 'Are you sure you want to submit the form?');
        },
        /** @returns Node of the action bar where the reset and submit buttons reside. */
        actionBarNode() {
            if (!this.showReset && !this.showSubmit) {
                return utils.emptyVNode();
            }
            return (<div class={[
                    'col-span-full flex items-stretch justify-start min-h-9',
                    this.actionBarClass,
                ]}>
                        {utils.renderScopedSlot(undefined, this.$scopedSlots.actionBar, utils.emptyVNode)}
                        {utils.ifOrEmpty(this.showReset, () => (<button.Button type="reset" semantic="critical" prominence={3} buttonClasses="px-2" disabled={this.maybeDisabledReason.orDefault(this.resetDisabledReason)} confirm={this.resetButtonConfirmProp} action={evt => {
                        if (this.confirmReset !== false) {
                            // When reset requires user confirmation, prevent the default and call reset form manually.
                            evt.preventDefault();
                            this.resetForm();
                        }
                    }}>
                                Reset
                            </button.Button>))}
                        <div class="grow"/>
                        <button.PromiseButton ref="submitButton" label={this.submitLabel} action={this.submitForm} buttonClasses="px-2" showIssuesInPopover={false} disabled={this.maybeDisabledReason.orDefault(this.submitDisabledReason)} loadingAlertMessage={this.submitLoadingMessage} successAlertMessage={this.submitSuccessMessage} onError={this.handleSubmitError} onWarning={this.handleSubmitWarning} confirm={this.submitButtonConfirmProp}/>
                    </div>);
        },
    },
    /** @returns Node of the Root. */
    render() {
        return (<form ref="formElement" data-cy={constants.nameRoot} class={[
                constants.nameRoot,
                {
                    stacked: 'form-layout-stacked',
                    row: 'form-layout-row',
                    custom: '',
                }[this.layout],
            ]} onSubmit={evt => {
                evt.preventDefault();
            }} onKeydown={evt => {
                if (this.submitOnEnter === false && // If this default behavior is disabled,
                    this.submitOnCtrlOrCommandEnter && // but we still want ctrl+enter to submit.
                    evt.key === 'Enter' &&
                    utils.isCtrlOrCmd(evt)) {
                    Maybe.fromNullable(utils.getRefAsHTMLElement(this.$refs.submitButton)).ifJust($el => $el.click());
                }
            }} onReset={evt => {
                evt.preventDefault();
                this.resetForm();
            }}>
                    {utils.ifOrEmpty(this.submitOnEnter === false, () => (
            /*
            This disabled and hidden submit button is the spec-compliant way of disabling
            the default "submit on enter" behavior of the <form> elements.

            See 4.10.22.2 Implicit Submission at https://www.w3.org/TR/2018/SPSD-html5-20180327/forms.html#implicit-submission

            When the user hits the enter key in <form> elements, browsers find the first
            submit button in a <form> and click it. If this found submit button is disabled,
            then the submit will not happen.

            We do not render this button if the prop `submitOnEnter` is true.

            biome-ignore lint/a11y/noAriaHiddenOnFocusable: This button is not meant to be fosued.
            */
            <button type="submit" disabled class="hidden" aria-hidden="true"/>))}

                    {this.$slots.default}
                    {utils.ifJustOrEmpty(this.error, errorEvent => (<Message role="alert" data-cy="cg-form-error" class="col-span-full" semantic="critical" variant="box">
                            {utils.renderScopedSlot(errorEvent.error, this.$scopedSlots.formError, () => errorEvent.error.message)}
                        </Message>))}
                    {utils.ifJustOrEmpty(this.warnings, warningEvent => (<Message data-cy="cg-form-warnings" class="col-span-full" semantic="highlighted" variant="box">
                            <ul class="list-none m-0 p-0 mb-2 space-y-2">
                                {warningEvent.warnings.map(w => (<li key={JSON.stringify(w)}>{w.text}</li>))}
                            </ul>
                            <button.Button data-cy="dismiss-warnings" class="ml-auto min-h-9 px-2" action={() => {
                    warningEvent.dismiss({ focusButtonAfterClose: false });
                    this.warnings = Maybe.empty();
                }} semantic="highlighted" prominence={5} label="Dismiss"/>
                        </Message>))}
                    {this.actionBarNode}
                </form>);
    },
}));
